class LogoScrollMarquee {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.track = wrapper.querySelector('.logo-scroll__track--animated');
    this.templateSet = this.track?.querySelector('.logo-scroll__set');
    this._prepareRaf = null;
    this._animateRaf = null;

    if (!this.track || !this.templateSet) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.position = 0;
    this.setWidth = 0;
    this.scrollDuration = 30;
    this.lastTimestamp = null;
    this.isAnimating = false;
    this.cloneCount = 0;

    this.onResize = () => this.schedulePrepare();
    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(this.wrapper);
    this.resizeObserver.observe(this.templateSet);

    this.track.querySelectorAll('img').forEach((img) => {
      if (img.complete) return;
      img.addEventListener('load', this.onResize, { once: true });
      img.addEventListener('error', this.onResize, { once: true });
    });

    this.schedulePrepare();
  }

  schedulePrepare() {
    cancelAnimationFrame(this._prepareRaf);
    this._prepareRaf = requestAnimationFrame(() => this.prepare());
  }

  getScrollDuration() {
    const value = getComputedStyle(this.wrapper).getPropertyValue('--scroll-duration').trim();
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 30;
  }

  stopAnimation() {
    this.isAnimating = false;
    this.lastTimestamp = null;
    cancelAnimationFrame(this._animateRaf);
    this._animateRaf = null;
  }

  resetClones() {
    this.stopAnimation();
    this.track.querySelectorAll('.logo-scroll__set[aria-hidden="true"]').forEach((set) => set.remove());
    this.track.classList.remove('logo-scroll__track--ready');
    this.track.style.removeProperty('transform');
    this.setWidth = 0;
    this.cloneCount = 0;
  }

  createClone() {
    const clone = this.templateSet.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('role');
    clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
    return clone;
  }

  measureSetWidth() {
    const sets = this.track.querySelectorAll('.logo-scroll__set');
    if (sets.length < 2) return 0;
    return sets[1].offsetLeft - sets[0].offsetLeft;
  }

  applyTransform() {
    this.track.style.transform = `translate3d(${this.position}px, 0, 0)`;
  }

  animate(timestamp) {
    if (!this.isAnimating) return;

    if (this.lastTimestamp === null) {
      this.lastTimestamp = timestamp;
    }

    const delta = (timestamp - this.lastTimestamp) / 1000;
    this.lastTimestamp = timestamp;

    this.position -= (this.setWidth / this.scrollDuration) * delta;

    if (this.position <= -this.setWidth) {
      this.position += this.setWidth;
    }

    this.applyTransform();
    this._animateRaf = requestAnimationFrame((time) => this.animate(time));
  }

  startAnimation() {
    if (this.setWidth <= 0 || this.isAnimating) return;
    this.scrollDuration = this.getScrollDuration();
    this.isAnimating = true;
    this.lastTimestamp = null;
    this._animateRaf = requestAnimationFrame((time) => this.animate(time));
  }

  prepare() {
    const viewportWidth = this.wrapper.clientWidth;
    const provisionalWidth = this.templateSet.getBoundingClientRect().width;

    if (provisionalWidth <= 0 || viewportWidth <= 0) return;

    const cloneCount = Math.max(1, Math.ceil(viewportWidth / provisionalWidth));
    const existingClones = this.track.querySelectorAll('.logo-scroll__set[aria-hidden="true"]');

    if (existingClones.length === cloneCount) {
      const setWidth = this.measureSetWidth();
      if (setWidth > 0 && setWidth === this.setWidth) {
        this.track.classList.add('logo-scroll__track--ready');
        if (!this.isAnimating) this.startAnimation();
        return;
      }
    }

    const previousSetWidth = this.setWidth;
    const scrolled =
      previousSetWidth > 0 ? Math.abs(this.position) % previousSetWidth : 0;

    this.resetClones();

    for (let i = 0; i < cloneCount; i += 1) {
      this.track.appendChild(this.createClone());
    }

    const setWidth = this.measureSetWidth();
    if (setWidth <= 0) return;

    this.setWidth = setWidth;
    this.cloneCount = cloneCount;

    const resumedOffset =
      previousSetWidth > 0 ? (scrolled / previousSetWidth) * setWidth : 0;
    this.position = resumedOffset === 0 ? 0 : -resumedOffset;

    this.track.classList.add('logo-scroll__track--ready');
    this.applyTransform();
    this.startAnimation();
  }

  destroy() {
    cancelAnimationFrame(this._prepareRaf);
    this.resetClones();
    this.resizeObserver?.disconnect();
  }
}

function initLogoScrollMarquees(root = document) {
  root.querySelectorAll('.logo-scroll__track-wrapper--scroll:not([data-marquee-init])').forEach((wrapper) => {
    wrapper.setAttribute('data-marquee-init', '');
    wrapper._logoScrollMarquee = new LogoScrollMarquee(wrapper);
  });
}

function destroyLogoScrollMarquees(root) {
  root.querySelectorAll('.logo-scroll__track-wrapper--scroll[data-marquee-init]').forEach((wrapper) => {
    wrapper._logoScrollMarquee?.destroy();
    delete wrapper._logoScrollMarquee;
    wrapper.removeAttribute('data-marquee-init');
  });
}

document.addEventListener('DOMContentLoaded', () => initLogoScrollMarquees());

if (document.readyState !== 'loading') {
  initLogoScrollMarquees();
}

document.addEventListener('shopify:section:load', (event) => {
  initLogoScrollMarquees(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  destroyLogoScrollMarquees(event.target);
});
